import formErrors from '../support/formErrors';

export default (
    { publishable_key, stripeAccount, return_url, elementType, shouldRedirect = true },
    initOnDOM = true,
    embedded = false,
    deviceName = null,
) => ({
    stripe: null,
    elements: null,
    paymentClientSecret: null,
    returnUrl: return_url,
    shouldRedirect: shouldRedirect,
    selectedType: null,
    stripeElements: {
        card: {
            type: 'card',
            inputs: [
                { id: 'card-number', type: 'cardNumber', options: { showIcon: true } },
                { id: 'card-expiry', type: 'cardExpiry', options: { placeholder: 'MM/YY' } },
                {
                    id: 'card-cvc',
                    type: 'cardCvc',
                    options: { placeholder: 'Security Code (CVC)' },
                },
            ],
            style: {
                base: {
                    fontSize: '14px',
                    '::placeholder': {
                        color: 'rgb(150 151 152)',
                    },
                },
                invalid: {
                    iconColor: '#CC2936',
                    color: 'rgb(50 10 11)',
                },
            },
            validationErrorMap: {
                incomplete_number: 'card-number',
                incomplete_expiry: 'card-expiry',
                incomplete_cvc: 'card-cvc',
            },
        },
        payment: {
            id: 'payment-element',
            type: 'payment',
            options: {
                layout: 'tabs',
            },
            appearance: {
                theme: 'flat',
                labels: 'floating',
                variables: {
                    fontFamily: '"Swiss 721", sans-serif',
                    fontLineHeight: '1.5',
                    borderRadius: '12px',
                    fontSizeBase: '14px',
                    fontSizeSm: '14px',
                    colorBackground: '#ECECEC',
                    colorPrimary: '#3887D9',
                    colorIconTabSelected: '#3887D9',
                    colorDanger: '#962523',
                },
                rules: {
                    '.Block': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        padding: '12px',
                    },
                    '.Input::placeholder': {
                        color: '#969798',
                    },
                    '.Input:focus': {
                        borderColor: '#797A7C',
                        outline: '2px solid transparent',
                        outlineOffset: '2px',
                        boxShadow: '0 0 0 1px rgb(121 122 124)',
                    },
                    '.Input': {
                        backgroundColor: 'transparent',
                        borderRadius: '10px',
                        borderColor: 'rgb(30 31 31)',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        padding: '8px 12px',
                        transition: 'all 100ms ease-in-out',
                        color: '#000',
                    },
                    '.Input--invalid': {
                        boxShadow: 'none',
                        color: '#320A0B',
                    },
                    '.Input--invalid:focus': {
                        borderColor: '#B82F2B',
                        boxShadow: '0 0 0 1px #B82F2B',
                    },
                    '.Input:disabled, .Input--invalid:disabled': {
                        color: 'lightgray',
                    },
                    '.Tab': {
                        padding: '10px 12px 8px 12px',
                        border: 'none',
                    },
                    '.Tab:hover': {
                        border: 'none',
                        boxShadow:
                            '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
                    },
                    '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
                        color: 'var(--colorPrimary)',
                        border: 'none',
                        backgroundColor: '#fff',
                        boxShadow:
                            '0 0 0 1.5px var(--colorPrimary), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
                    },
                    '.Label': {
                        fontWeight: '500',
                    },
                    '.Label--resting': {
                        color: '#969798',
                        fontSize: 'var(--fontSizeSm)',
                        fontWeight: '500',
                    },
                    '.Label--floating': {
                        fontSize: 'var(--fontSizeXs)',
                        fontWeight: '500',
                        color: '#3D3D3E',
                    },
                },
            },
        },
    },

    init() {
        document.addEventListener('DOMContentLoaded', () => {
            if (!initOnDOM) {
                return;
            }

            this.setStripe().mountElements();
        });
    },

    setStripe(mode = 'payment', clientSecret = null) {
        this.stripe = Stripe(
            publishable_key,
            stripeAccount
                ? {
                      stripeAccount,
                  }
                : {},
        );

        const options = {};

        if (elementType === 'payment') {
            options.appearance = this.stripeElements[elementType].appearance;

            if (mode === 'setup') {
                options.currency = 'usd';
                options.mode = mode;
            } else {
                options.clientSecret = clientSecret;
            }
        }

        this.elements = this.stripe.elements(options);

        return this;
    },

    mountElements() {
        const stripeElement =
            elementType && this.stripeElements.hasOwnProperty(elementType)
                ? this.stripeElements[elementType]
                : this.stripeElements['card'];

        if (stripeElement.type === 'payment') {
            let element = this.elements.getElement(stripeElement.type);
            if (!element) {
                element = this.elements.create(stripeElement.type, stripeElement.options);
            }

            element.on('change', ({ value }) => {
                this.selectedType = value.type;
            });

            element.mount(`#${stripeElement.id}`);

            return;
        }

        stripeElement.inputs.forEach(({ id, type, options }) => {
            let element = this.elements.getElement(type);
            if (!element) {
                element = this.elements.create(type, {
                    ...options,
                    style: stripeElement.style,
                });
            }

            element.on('change', (event) => {
                if (!event.error) {
                    this.forgetErrors(id);
                    return;
                }

                this.setErrors({
                    [id]: event.error.message,
                });
            });

            element.mount(`#${id}`);
        });

        return this;
    },

    async startPaymentElement() {
        const clientSecret = await this.$wire.getPaymentIntentClientSecret();

        if (!clientSecret) {
            return false;
        }

        if (this.paymentClientSecret === clientSecret) {
            try {
                await this.elements?.fetchUpdates();
            } catch (error) {
                console.error(error);
            }

            return true;
        }

        this.paymentClientSecret = clientSecret;
        this.setStripe('payment', clientSecret).mountElements();

        return true;
    },

    async confirmPayment() {
        const { result } = await this.elements.fetchUpdates();
        if (result?.error) {
            this.$dispatch('processed');
            this.handleError(result.error.message, 'An unexpected error occurred.');
            return;
        }

        await this.stripe
            .confirmPayment({
                elements: this.elements,
                redirect: 'if_required',
                confirmParams: {
                    return_url: `${this.returnUrl}?clear_cache=false`,
                },
            })
            .then(({ error, paymentIntent }) => {
                if (error) {
                    this.$dispatch('processed');
                    this.$dispatch('release-reserved-spot');
                    this.handleError(error.message, 'An unexpected error occurred.');
                    return;
                }

                if (!this.shouldRedirect) {
                    this.$dispatch('processed');
                    return;
                }

                this.redirectToUrl(
                    paymentIntent.id,
                    _.get(paymentIntent, 'last_payment_error.message'),
                );
            });
    },

    async confirmSetup() {
        const clientSecret = await this.$wire.getSetupIntentClientSecret();

        const { setupIntent, error } = await this.stripe.confirmSetup({
            elements: this.elements,
            clientSecret,
            confirmParams: {
                return_url: this.returnUrl,
            },
            redirect: this.shouldRedirect ? 'always' : 'if_required',
        });

        if (!error) {
            return setupIntent;
        }

        this.handleError(error.message);
    },

    redirectToUrl(payment_intent, error = false) {
        let data = {
            error,
            embedded,
            payment_intent,
            redirect: location.href,
        };

        if (deviceName) {
            data.device_name = deviceName;
        }

        const params = new URLSearchParams(data);

        location.href = `${this.returnUrl}?${params.toString()}`;
    },

    async confirmCardPayment() {
        const clientSecret = await this.$wire.getPaymentIntentClientSecret();

        const { paymentIntent, error } = await this.stripe.confirmCardPayment(clientSecret, {
            payment_method: {
                card: this.elements.getElement('cardNumber'),
                billing_details: {
                    name: this.$refs.cardName.value,
                    address: {
                        postal_code: this.$refs.zip.value,
                    },
                },
            },
        });

        if (error) {
            this.$dispatch('processed');
            this.$dispatch('release-reserved-spot');

            if (_.get(this.stripeElements['card'].validationErrorMap, error.code)) {
                this.setErrors({
                    [this.stripeElements['card'].validationErrorMap[error.code]]: error.message,
                });
            } else {
                this.handleError(error.message);
            }

            return;
        }

        this.$wire.finishCheckout(paymentIntent.payment_method);
    },

    async confirmCardSetup() {
        const clientSecret = await this.$wire.getSetupIntentClientSecret();

        const { setupIntent, error } = await this.stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                card: this.elements.getElement('cardNumber'),
                billing_details: {
                    name: this.$refs.cardName.value,
                    address: {
                        postal_code: this.$refs.zip.value,
                    },
                },
            },
        });

        if (error) {
            this.$wire.set('processing', false);

            if (_.get(this.stripeElements['card'].validationErrorMap, error.code)) {
                this.setErrors({
                    [this.stripeElements['card'].validationErrorMap[error.code]]: error.message,
                });
            } else {
                this.handleError(error.message);
            }

            return;
        }

        this.$wire.updateDefaultPayment(setupIntent.payment_method);
    },

    async validateElements() {
        const { error } = await this.elements.submit();
        return error ? false : true;
    },

    async startElements() {
        const step = document.querySelector(`[step*="3"]`);

        if (step.style.display !== 'none') {
            this.$el.removeAttribute('hidden');

            if (this.elements) {
                return;
            }

            const clientSecret = await this.$wire.getPaymentIntentClientSecret();

            this.setStripe('payment', clientSecret).mountElements();
        }

        const observer = new MutationObserver((mutations) => {
            mutations.forEach(async (mutation) => {
                if (mutation.attributeName !== 'style') {
                    return;
                }

                if (mutation.target.style.display !== 'none') {
                    this.$el.removeAttribute('hidden');

                    if (this.elements) {
                        return;
                    }

                    const clientSecret = await this.$wire.getPaymentIntentClientSecret();

                    this.setStripe('payment', clientSecret).mountElements();
                }
            });
        });
        observer.observe(step, { attributes: true });
    },

    clearElements() {
        this.elements?.getElement('payment')?.clear();
    },

    handleError(description, title = 'There was an error with your card.') {
        console.error(description);

        window.$wireui.notify({
            title,
            description,
            icon: 'warning',
        });
    },

    ...formErrors,
});
